"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { useAccessToken } from "@/hooks";
import { useAuth } from "@/lib/contexts";
import { UserRoles } from "@/lib/types/user-roles";
import { getActiveRole } from "@/lib/utils/roles";
import { sendStaffOnlineHeartbeat } from "./staff-online";

const HEARTBEAT_MS = 30_000;

function isDashboardRole(role: string | null): boolean {
  return (
    role === UserRoles.Admin ||
    role === UserRoles.Staff ||
    role === UserRoles.Teacher
  );
}

/** Tells the API this dashboard tab is open. The role travels in the body. */
export function StaffOnlineHeartbeat() {
  const user = useAuth();
  const accessToken = useAccessToken();
  const pathname = usePathname();
  const supportedRef = useRef(true);
  const role = getActiveRole(user);
  const enabled =
    Boolean(accessToken) &&
    pathname?.startsWith("/admin") === true &&
    isDashboardRole(role);

  useEffect(() => {
    if (!enabled || !accessToken) return undefined;

    let cancelled = false;

    const beat = async () => {
      if (!supportedRef.current) return;
      try {
        const ok = await sendStaffOnlineHeartbeat(accessToken, role);
        if (!cancelled && !ok) supportedRef.current = false;
      } catch {
        // The next beat tries again. A closed tab simply stops sending.
      }
    };

    void beat();
    const intervalId = window.setInterval(() => void beat(), HEARTBEAT_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [accessToken, enabled, role]);

  return null;
}
